import { Alert, Modal, Text, Flex, Box, Stack, Tooltip, Tabs } from '@mantine/core';
import { Button as ButtonV2 } from '@dealstack/frontend/shared/components/fundament/Button/Button.v2';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useTransactionFromDocumentModalController } from './useTransactionFromDocumentModalController';
import { TransactionFromDocumentModalContext } from './TransactionFromDocumentModalContext';
import { ProcessingState } from './components/ProcessingState';
import { DocumentUploader } from './components/DocumentUploader';
import { ExtractedDataDisplay } from './components/ExtractedDataDisplay';
import { DocumentList } from './components/DocumentList';
import { BatchList } from './components/BatchList';
import { BatchTransactionList } from './components/BatchTransactionList';
import { DocumentAnalysisData } from '@dealstack/shared/lib/common/business/documentAnalysis';
import { TaskStatus } from '@dealstack/shared/lib/generated';
import type { RichCanonicalEntity } from '@dealstack/shared/lib/temporal/documentExtraction/types';
import { useNavigate, useParams, generatePath } from 'react-router-dom';
import { DEAL_ROUTES, ROUTES } from '@dealstack/frontend/app/pages/Routing/routes';

const ExtractionCompleteState = ({
	batchId: _batchId,
	dealId: _dealId,
	onNavigate,
	tCapManage,
}: {
	batchId: string;
	dealId: string;
	onNavigate: () => void;
	tCapManage: (key: string) => string;
}) => (
	<Flex
		direction="column"
		align="center"
		justify="center"
		style={{ height: '100%', minHeight: 300 }}
		gap="lg"
	>
		<Icon variant="success-checkmark-circle" size="xl" />
		<Text size="lg" fw={600}>
			{tCapManage('transactions.add_from_document.extraction_complete')}
		</Text>
		<Text size="sm" color="dimmed">
			{tCapManage('transactions.add_from_document.extraction_complete_description')}
		</Text>
		<ButtonV2 variant="primary" size="m" buttonTheme="light" onClick={onNavigate}>
			{tCapManage('transactions.add_from_document.open_analysis')}
		</ButtonV2>
	</Flex>
);

interface TransactionFromDocumentModalProps {
	opened: boolean;
	onClose: () => void;
	onProceedToBulkImport?: (data: DocumentAnalysisData[]) => void;
	knownPersons?: RichCanonicalEntity[];
	knownSecurities?: RichCanonicalEntity[];
}

export const TransactionFromDocumentModal = ({
	opened,
	onClose,
	onProceedToBulkImport,
	knownPersons,
	knownSecurities,
}: TransactionFromDocumentModalProps) => {
	const navigate = useNavigate();
	const { dealId } = useParams<{ dealId: string }>();
	const tCapManage = useTranslationCapManage();
	const controller = useTransactionFromDocumentModalController({
		knownPersons,
		knownSecurities,
	});
	const {
		documents,
		handleFileDrop,
		isProcessing,
		clearFile,
		processDocuments,
		analysisResult,
		getAllTransactions,
		processedDocumentsCount,
		hasAnyResults,
		isSelectionRequired,
		extractionProgress,
		isExtractionPollingStalled,
		batches,
		isBatchesLoading,
		selectedBatchId,
		selectedBatchStatus,
		selectBatch,
		deselectBatch,
		batchTransactions,
		isBatchTransactionsLoading,
		refetchBatches,
		batchId,
	} = controller;

	const handleClose = () => {
		clearFile();
		onClose();
	};

	const hasUnprocessedDocuments =
		documents.length > 0 && processedDocumentsCount < documents.length;

	const isExtractionDone = extractionProgress?.status === TaskStatus.DONE;

	// Footer content depends on processing state
	const renderFooter = () => {
		if (isProcessing) {
			// Processing footer: Cancel | Notify | Run in background | Open analysis
			return (
				<Flex
					justify="space-between"
					align="center"
					px="xl"
					py="md"
					sx={(theme) => ({
						borderTop: `1px solid ${theme.colors.gray[4]}`,
						backgroundColor: theme.colors.gray[0],
					})}
				>
					<Flex align="center" gap="sm">
						<ButtonV2
							variant="tertiary"
							size="m"
							buttonTheme="light"
							onClick={handleClose}
						>
							{tCapManage('common.cancel')}
						</ButtonV2>
						<ButtonV2 variant="ghost" size="m" buttonTheme="light" disabled>
							{tCapManage('transactions.add_from_document.notify_when_complete')}
						</ButtonV2>
					</Flex>
					<Flex align="center" gap="sm">
						<ButtonV2 variant="secondary" size="m" buttonTheme="light" disabled>
							{tCapManage('transactions.add_from_document.run_in_background')}
						</ButtonV2>
						<ButtonV2
							variant="primary"
							size="m"
							buttonTheme="light"
							disabled={!isExtractionDone || !batchId}
							onClick={() => {
								if (isExtractionDone && batchId && dealId) {
									handleClose();
									navigate(
										generatePath(
											`${ROUTES.DEAL}${DEAL_ROUTES.BATCH_ANALYSIS}`,
											{ dealId, batchId }
										)
									);
								}
							}}
						>
							{tCapManage('transactions.add_from_document.open_analysis')}
						</ButtonV2>
					</Flex>
				</Flex>
			);
		}

		// Default footer: file count | Cancel + Analyse
		return (
			<Flex
				justify="space-between"
				align="center"
				px="xl"
				py="md"
				sx={(theme) => ({
					borderTop: `1px solid ${theme.colors.gray[4]}`,
					backgroundColor: theme.colors.gray[0],
				})}
			>
				<Flex align="center" gap="xs">
					{documents.length > 0 && (
						<>
							<Text size="xl" fw={600} color="dark.8">
								{documents.length}
							</Text>
							<Flex direction="column" gap={0}>
								<Text size="sm" fw={500} color="dark.0">
									{tCapManage('transactions.add_from_document.files')}
								</Text>
								<Text size="sm" color="dark.0">
									{tCapManage('transactions.add_from_document.uploaded')}
								</Text>
							</Flex>
						</>
					)}
				</Flex>
				<Flex align="center" gap="sm">
					<ButtonV2 variant="ghost" size="m" buttonTheme="light" onClick={handleClose}>
						{tCapManage('common.cancel')}
					</ButtonV2>
					<ButtonV2
						variant="primary"
						size="m"
						buttonTheme="light"
						onClick={() => {
							if (hasAnyResults && !isSelectionRequired()) {
								const allTransactions = getAllTransactions();
								if (onProceedToBulkImport && allTransactions.length > 0) {
									onProceedToBulkImport(allTransactions);
									handleClose();
								}
							} else if (hasUnprocessedDocuments) {
								void processDocuments();
							}
						}}
						disabled={documents.length === 0 || isProcessing}
					>
						{tCapManage('transactions.add_from_document.analyse_documents')}
					</ButtonV2>
				</Flex>
			</Flex>
		);
	};

	return (
		<Modal
			opened={opened}
			onClose={handleClose}
			title={
				<Text size="H3Thin">
					{tCapManage('transactions.add_from_document.modal_title')}
				</Text>
			}
			size="80vw"
			styles={{
				content: {
					minHeight: '80vh',
					display: 'flex',
					flexDirection: 'column',
				},
				body: {
					padding: 0,
					display: 'flex',
					flexDirection: 'column',
					flex: 1,
				},
			}}
			transitionProps={{ transition: 'fade', duration: 200 }}
		>
			<TransactionFromDocumentModalContext.Provider value={controller}>
				<Tabs
					defaultValue="upload"
					variant="default"
					onChange={(tab) => {
						if (tab === 'batches') {
							void refetchBatches();
						}
					}}
					style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
					styles={(theme) => ({
						tabsList: {
							borderBottom: 'none',
							padding: '0.5rem 1rem',
							gap: '0.25rem',
						},
						tab: {
							borderRadius: '0.375rem',
							fontWeight: 400,
							fontSize: '0.875rem',
							border: '1px solid transparent',
							backgroundColor: 'transparent',
							color: theme.colors.dark[0],
							padding: '0.375rem 0.625rem',
							'&[data-active]': {
								border: `1px solid ${theme.colors.gray[4]}`,
								backgroundColor: 'transparent',
								color: theme.colors.dark[8],
								fontWeight: 500,
							},
							'&:hover:not([data-active]):not(:disabled)': {
								backgroundColor: theme.colors.gray[0],
							},
							'&:disabled': {
								opacity: 0.5,
								backgroundColor: 'transparent',
								color: theme.colors.gray[8],
							},
						},
						panel: {
							flex: 1,
							display: 'flex',
							flexDirection: 'column',
						},
					})}
				>
					<Tabs.List px="md" pt="xs">
						<Tabs.Tab
							value="upload"
							leftSection={<Icon variant="upload-v3-thin" size="lg" />}
						>
							{tCapManage('transactions.add_from_document.tab_upload')}
						</Tabs.Tab>
						<Tooltip
							label={tCapManage('transactions.add_from_document.coming_soon')}
							position="bottom"
							withArrow
						>
							<Tabs.Tab
								value="library"
								leftSection={<Icon variant="folder" size="lg" />}
								disabled
							>
								{tCapManage('transactions.add_from_document.tab_library')}
							</Tabs.Tab>
						</Tooltip>
						<Tabs.Tab
							value="batches"
							leftSection={<Icon variant="document-v3" size="lg" />}
						>
							{tCapManage('transactions.add_from_document.tab_batches_with_count', {
								count: batches.length,
							})}
						</Tabs.Tab>
					</Tabs.List>

					<Tabs.Panel value="upload" style={{ flex: 1 }}>
						<Box p="md" style={{ flex: 1 }}>
							{isProcessing && !isExtractionDone && (
								<>
									{isExtractionPollingStalled && (
										<Alert color="orange" mb="md">
											{tCapManage('batch_analysis.extraction_status_stalled')}
										</Alert>
									)}
									<ProcessingState />
								</>
							)}

							{isProcessing && isExtractionDone && batchId && dealId && (
								<ExtractionCompleteState
									batchId={batchId}
									dealId={dealId}
									onNavigate={() => {
										handleClose();
										navigate(
											generatePath(
												`${ROUTES.DEAL}${DEAL_ROUTES.BATCH_ANALYSIS}`,
												{ dealId, batchId }
											)
										);
									}}
									tCapManage={tCapManage}
								/>
							)}

							{!isProcessing && (
								<Stack gap="xl" style={{ height: '100%' }}>
									<DocumentUploader
										onDropFile={handleFileDrop}
										isProcessing={isProcessing}
									/>

									{documents.length > 0 && <DocumentList />}

									{analysisResult && <ExtractedDataDisplay />}
								</Stack>
							)}
						</Box>
					</Tabs.Panel>

					<Tabs.Panel value="library">
						<Box p="md" />
					</Tabs.Panel>

					<Tabs.Panel value="batches" style={{ flex: 1 }}>
						<Box p="md" style={{ flex: 1 }}>
							{!selectedBatchId && (
								<BatchList
									batches={batches}
									isLoading={isBatchesLoading}
									onSelectBatch={(id) => {
										const batch = batches.find((b) => b.id === id);
										if (!batch) {
											showToastV2({
												state: 'error',
												title: tCapManage(
													'transactions.add_from_document.error_title'
												),
												message:
													tCapManage(
														'transactions.add_from_document.batch_not_found'
													) ||
													'Batch not found. Please refresh and try again.',
											});
											return;
										}
										if (
											(batch.status === TaskStatus.DONE ||
												batch.status === TaskStatus.ERROR) &&
											dealId
										) {
											handleClose();
											navigate(
												generatePath(
													`${ROUTES.DEAL}${DEAL_ROUTES.BATCH_ANALYSIS}`,
													{ dealId, batchId: id }
												)
											);
										} else {
											void selectBatch(id, batch.status, batch.documents);
										}
									}}
								/>
							)}

							{selectedBatchId && selectedBatchStatus === TaskStatus.DONE && (
								<BatchTransactionList
									documents={batchTransactions}
									isLoading={isBatchTransactionsLoading}
									onBack={deselectBatch}
								/>
							)}

							{selectedBatchId &&
								selectedBatchStatus !== TaskStatus.DONE &&
								isProcessing &&
								!isExtractionDone && (
									<>
										<Box mb="md" style={{ alignSelf: 'flex-start' }}>
											<ButtonV2
												variant="ghost"
												size="xs"
												buttonTheme="light"
												onClick={deselectBatch}
											>
												{tCapManage(
													'transactions.add_from_document.back_to_batches'
												)}
											</ButtonV2>
										</Box>
										<ProcessingState />
									</>
								)}

							{selectedBatchId &&
								isProcessing &&
								isExtractionDone &&
								batchId &&
								dealId && (
									<ExtractionCompleteState
										batchId={batchId}
										dealId={dealId}
										onNavigate={() => {
											handleClose();
											navigate(
												generatePath(
													`${ROUTES.DEAL}${DEAL_ROUTES.BATCH_ANALYSIS}`,
													{ dealId, batchId }
												)
											);
										}}
										tCapManage={tCapManage}
									/>
								)}
						</Box>
					</Tabs.Panel>
				</Tabs>

				{renderFooter()}
			</TransactionFromDocumentModalContext.Provider>
		</Modal>
	);
};
